/**
 * EntregadorAuthContext - Contexto de autenticação segura para entregadores
 * 
 * SEGURANÇA: Usa session_token UUID armazenado no banco (não mais base64 forjável).
 * Autenticação via WhatsApp + OTP através da Edge Function entregador-login.
 */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { SUPABASE_URL } from '@/lib/supabaseEdge';

export interface EntregadorSession {
  id: number;
  nome: string;
  whatsapp: string;
  unidadeId: number;
  modoTransporte: string;
  token: string; // UUID session_token do banco
  expiresAt: string;
}

interface EntregadorAuthContextType {
  session: EntregadorSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (sessionData: {
    id: number;
    nome: string;
    whatsapp: string;
    unidade_id: number;
    modo_transporte: string;
    session_token: string;
    session_expires_at: string;
  }) => void;
  logout: () => void;
  updateLocation: (lat: number, lng: number) => void;
}

const EntregadorAuthContext = createContext<EntregadorAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'sf_entregador_session';

// Validar se sessão ainda não expirou
const isSessionValid = (expiresAt: string): boolean => {
  try {
    return new Date(expiresAt) > new Date();
  } catch {
    return false;
  }
};

export function EntregadorAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<EntregadorSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restaurar sessão do localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as EntregadorSession;
        if (parsed.token && parsed.expiresAt && isSessionValid(parsed.expiresAt)) {
          setSession(parsed);
        } else {
          console.log('[EntregadorAuth] Sessão expirada, removendo');
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('[EntregadorAuth] Erro ao restaurar sessão:', error);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback((sessionData: {
    id: number;
    nome: string;
    whatsapp: string;
    unidade_id: number;
    modo_transporte: string;
    session_token: string;
    session_expires_at: string;
  }) => {
    const newSession: EntregadorSession = {
      id: sessionData.id,
      nome: sessionData.nome,
      whatsapp: sessionData.whatsapp,
      unidadeId: sessionData.unidade_id,
      modoTransporte: sessionData.modo_transporte,
      token: sessionData.session_token,
      expiresAt: sessionData.session_expires_at,
    };
    
    setSession(newSession);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
    
    console.log('[EntregadorAuth] Login realizado:', sessionData.nome);
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    localStorage.removeItem(STORAGE_KEY);
    console.log('[EntregadorAuth] Logout realizado');
  }, []);

  const updateLocation = useCallback(async (lat: number, lng: number) => {
    if (!session) return;
    
    // Verificar se sessão ainda é válida antes de fazer request
    if (!isSessionValid(session.expiresAt)) {
      console.warn('[EntregadorAuth] Sessão expirada, fazendo logout');
      logout();
      return;
    }
    
    // Atualizar localização via Edge Function segura
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/entregador-atualizar-localizacao`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-entregador-token': session.token, // UUID seguro do banco
          },
          body: JSON.stringify({ lat, lng }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          // Sessão inválida no servidor, fazer logout
          console.warn('[EntregadorAuth] Sessão inválida no servidor, fazendo logout');
          logout();
          return;
        }
        console.warn('[EntregadorAuth] Erro ao atualizar localização:', errorData.error || response.status);
      }
    } catch (error) {
      console.error('[EntregadorAuth] Erro ao atualizar localização:', error);
    }
  }, [session, logout]);

  return (
    <EntregadorAuthContext.Provider
      value={{
        session,
        isLoading,
        isAuthenticated: !!session && isSessionValid(session.expiresAt),
        login,
        logout,
        updateLocation,
      }}
    >
      {children}
    </EntregadorAuthContext.Provider>
  );
}

export function useEntregadorAuth() {
  const context = useContext(EntregadorAuthContext);
  if (!context) {
    throw new Error('useEntregadorAuth deve ser usado dentro de EntregadorAuthProvider');
  }
  return context;
}
